import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'motion/react';

interface SplitFlapNumberProps {
  value: string;
}

const CHAR_SET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.,/#*&!+-@%;:'\"()[]";

function FlipChar({ targetChar, delay, start }: { targetChar: string, delay: number, start: boolean }) {
  const [flipCount, setFlipCount] = useState(0);
  const [isDone, setIsDone] = useState(false);

  useEffect(() => {
    if (!start) return;

    let targetIndex = CHAR_SET.indexOf(targetChar);
    if (targetIndex === -1) targetIndex = CHAR_SET.length - 1; // Fallback
    
    const totalFlips = targetIndex;
    
    if (totalFlips === 0) {
      setIsDone(true);
      return;
    }
    
    let settleTimeout: ReturnType<typeof setTimeout> | null = null;
    let interval: ReturnType<typeof setInterval> | null = null;

    const timeout = setTimeout(() => {
      interval = setInterval(() => {
        setFlipCount(c => {
          if (c >= totalFlips) {
            if (interval) clearInterval(interval);
            settleTimeout = setTimeout(() => setIsDone(true), 110);
            return c;
          }
          return c + 1;
        });
      }, 100); // 100ms per flip (so it takes a moment to reach final char)
    }, delay * 150); // Staggered start based on index
    
    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
      if (settleTimeout) clearTimeout(settleTimeout);
    };
  }, [targetChar, delay, start]);

  const prevChar = CHAR_SET[Math.max(0, flipCount - 1) % CHAR_SET.length];
  const curChar = CHAR_SET[flipCount % CHAR_SET.length];
  
  // Animate only while actively flipping; unmount 3D layers once settled
  const isFlipping = flipCount > 0 && !isDone;

  return (
    <div
      className="relative inline-flex items-center justify-center w-[0.8em] h-[1.12em] bg-[#1a1a1a] border border-[#333] border-b-black mx-[2px] rounded overflow-hidden shadow-[inset_0_2px_6px_rgba(0,0,0,0.4),0_2px_8px_rgba(0,0,0,0.3)]"
      style={{ perspective: "400px", contain: "layout paint" }}
    >
      
      {/* Top half static (Next/Current Char) */}
      <div className="absolute top-0 left-0 w-full h-[50%] overflow-hidden bg-gradient-to-b from-[#252525] to-[#111] text-[#E6E6E6] z-0 rounded-t border-b border-black">
        <div className="absolute top-0 left-0 w-full h-[1.1em] flex items-center justify-center leading-none">
          <span className="text-[0.85em] not-italic font-mono font-bold text-center" style={{ textShadow: "0 1px 1px rgba(0,0,0,0.6)" }}>{curChar}</span>
        </div>
      </div>
      
      {/* Bottom half static (Previous Char) */}
      <div className="absolute bottom-0 left-0 w-full h-[50%] overflow-hidden bg-gradient-to-t from-[#1a1a1a] to-[#222] text-[#E6E6E6] z-0 rounded-b">
        <div className="absolute bottom-0 left-0 w-full h-[1.1em] flex items-center justify-center leading-none">
          <span className="text-[0.85em] not-italic font-mono font-bold text-center" style={{ textShadow: "0 -1px 1px rgba(0,0,0,0.6)" }}>{isFlipping ? prevChar : curChar}</span>
        </div>
      </div>

      {isFlipping && (
        <>
          {/* Flap Top (falling) */}
          <motion.div
            key={`top-${flipCount}`}
            initial={{ rotateX: 0 }}
            animate={{ rotateX: -90 }}
            transition={{ duration: 0.05, ease: "easeIn" }}
            style={{ originY: 1, backfaceVisibility: "hidden", willChange: "transform" }}
            className="absolute top-0 left-0 w-full h-[50%] overflow-hidden bg-gradient-to-b from-[#2a2a2a] to-[#111] z-20 text-[#E6E6E6] rounded-t border-b border-black shadow-[0_3px_3px_rgba(0,0,0,0.3)]"
          >
            <div className="absolute top-0 left-0 w-full h-[1.1em] flex items-center justify-center leading-none">
              <span className="text-[0.85em] not-italic font-mono font-bold text-center" style={{ textShadow: "0 1px 1px rgba(0,0,0,0.6)" }}>{prevChar}</span>
            </div>
          </motion.div>

          {/* Flap Bottom (revealing) */}
          <motion.div
            key={`bottom-${flipCount}`}
            initial={{ rotateX: 90 }}
            animate={{ rotateX: 0 }}
            transition={{ duration: 0.05, ease: "easeOut", delay: 0.05 }}
            style={{ originY: 0, backfaceVisibility: "hidden", willChange: "transform" }}
            className="absolute bottom-0 left-0 w-full h-[50%] overflow-hidden bg-gradient-to-t from-[#222] to-[#2a2a2a] z-20 text-[#E6E6E6] rounded-b shadow-[0_-3px_3px_rgba(0,0,0,0.3)]"
          >
            <div className="absolute bottom-0 left-0 w-full h-[1.1em] flex items-center justify-center leading-none">
              <span className="text-[0.85em] not-italic font-mono font-bold text-center" style={{ textShadow: "0 -1px 1px rgba(0,0,0,0.6)" }}>{curChar}</span>
            </div>
          </motion.div>
        </>
      )}

      {/* The Flap line (always visible on split-flap boards) */}
      <div className="absolute left-0 right-0 top-[50%] -translate-y-1/2 border-t-[1px] border-black z-30 pointer-events-none shadow-[0_1px_1px_rgba(0,0,0,0.4)]" />
      <div className="absolute left-0 right-0 top-[50%] border-t-[1px] border-white/10 z-30 pointer-events-none" />
      <div className="absolute inset-0 rounded pointer-events-none border-[1px] border-white/5 z-40" />
    </div>
  );
}

export function SplitFlapDisplay({ text }: { text: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <div ref={ref} className="inline-flex font-mono font-bold">
      {text.split('').map((char, i) => (
        <FlipChar key={`${i}-${char}`} targetChar={char} delay={i} start={isInView} />
      ))}
    </div>
  );
}
